import React, { useState, useEffect } from 'react';
import { collection, getDocs, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { FileText, Clock, CheckCircle, X, ChevronDown, Truck, AlertCircle, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useClickOutside from './hooks/useClickOutside.js';
import Spinner from './Spinner.js';
import { useMemo } from 'react';
import { isAdminUser, isOpsUser, isSalesManager, isSalesUser } from './accessControl.js';
import StatusBadge from './components/StatusBadge.js';

const Orders = ({ db, user }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [dispatchesMap, setDispatchesMap] = useState({}); // orderId -> dispatches array
  const [loading, setLoading] = useState(true);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const dropdownRef = useClickOutside(() => setActiveDropdownId(null));

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;
  const itemsPerPageOptions = [25, 50, 100];

  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedValue = Number(sessionStorage.getItem('orders_itemsPerPage'));
    return itemsPerPageOptions.includes(savedValue) ? savedValue : 25;
  });
  const [currentPage, setCurrentPage] = useState(() => {
    return Number(sessionStorage.getItem('orders_currentPage')) || 1;
  });
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const defaultFilters = {
    startDate: '',
    endDate: '',
    salesPerson: '',
    orderType: '',
    status: '',
    customerName: '',
  };
  const [filters, setFilters] = useState(() => {
    const savedFilters = sessionStorage.getItem('orders_filters');
    return savedFilters ? JSON.parse(savedFilters) : defaultFilters;
  });

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('orders_filters', JSON.stringify(filters));
    sessionStorage.setItem('orders_currentPage', currentPage.toString());
    sessionStorage.setItem('orders_itemsPerPage', itemsPerPage.toString());
  }, [filters, currentPage, itemsPerPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setCurrentPage(1);
  };

    // Filter and Sorting logic
    const filteredAndSortedOrders = useMemo(() => {
      let filterableOrders = [...orders];
  
      // Apply filters
      filterableOrders = filterableOrders.filter(order => {
        // Role based filtering: Ops cannot see draft orders
        if (isOpsUser(user) && order.status === 'draft') return false;
  
        // Date range filter
      if (filters.startDate || filters.endDate) {
          const orderDate = new Date(order.date || order.createdAt);
          orderDate.setHours(0, 0, 0, 0);

          if (filters.startDate) {
            const start = new Date(filters.startDate);
            start.setHours(0, 0, 0, 0);
            if (orderDate < start) return false;
          }

          if (filters.endDate) {
            const end = new Date(filters.endDate);
            end.setHours(0, 0, 0, 0);
            if (orderDate > end) return false;
          }
        }

      if (filters.customerName && order.customerName?.toLowerCase().indexOf(filters.customerName.toLowerCase()) === -1) return false;
      if (filters.salesPerson && order.salesPerson?.toLowerCase().indexOf(filters.salesPerson.toLowerCase()) === -1) return false;
      if (filters.orderType && order.orderType?.toLowerCase() !== filters.orderType.toLowerCase()) return false;
      if (filters.status) {
        if (filters.status === 'qc_pending_group') {
          if (!['packed_ready', 'invoice_pending', 'ewaybill_pending', 'invoice_ready', 'ewaybill_complete', 'dc_confirmed', 'qc_pending'].includes(order.status)) return false;
        } else if (order.status?.toLowerCase() !== filters.status.toLowerCase()) {
          return false;
        }
      }
      return true;
    });

    // Apply sorting
    if (sortColumn) {
      filterableOrders.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        if (sortColumn === 'createdAt') {
            const dateA = new Date(aValue);
            const dateB = new Date(bValue);
            if (sortDirection === 'asc') return dateA - dateB;
            return dateB - dateA;
        } else if (typeof aValue === 'string') {
            if (sortDirection === 'asc') return aValue.localeCompare(bValue);
            return bValue.localeCompare(aValue);
        } else {
            if (sortDirection === 'asc') return aValue - bValue;
            return bValue - aValue;
        }
      });
    }
    return filterableOrders;
  }, [orders, sortColumn, sortDirection, filters]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedOrders.length / itemsPerPage);
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedOrders.slice(startIndex, endIndex);
  }, [filteredAndSortedOrders, currentPage, itemsPerPage]);

  const filteredOrderValueTotal = useMemo(() => (
    filteredAndSortedOrders.reduce((sum, order) => {
      const orderValue = Number(order.orderValue);
      return sum + (Number.isFinite(orderValue) ? orderValue : 0);
    }, 0)
  ), [filteredAndSortedOrders]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page on sort change
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortDirection === 'asc' ? ' ▲' : ' ▼';
    }
    return '';
  };

  useEffect(() => {
    setLoading(true);
    
    // Subscribe to Orders
    const unsubOrders = onSnapshot(collection(db, "orders"), (querySnapshot) => {
      const ordersList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setOrders(ordersList);
      setLoading(false);
    });

    // Subscribe to All Dispatches to determine partial statuses
    const unsubDispatches = onSnapshot(collection(db, "dispatches"), (querySnapshot) => {
      const dMap = {};
      querySnapshot.forEach(doc => {
        const data = doc.data();
        if (data.orderId) {
          if (!dMap[data.orderId]) dMap[data.orderId] = [];
          dMap[data.orderId].push({ id: doc.id, ...data });
        }
      });
      setDispatchesMap(dMap);
    });

    return () => {
      unsubOrders();
      unsubDispatches();
    };
  }, [db]);

  const handleQuickStatusUpdate = async (orderId, newStatus) => {
    if (!isAdminUser(user)) return;

    try {
      const orderRef = doc(db, "orders", orderId);
      await updateDoc(orderRef, { 
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      setActiveDropdownId(null);
    } catch (error) {
      console.error("Error updating status: ", error);
      alert("Failed to update status.");
    }
  };

  const statusOptions = [
    { id: 'confirmed_order', label: 'Confirmed Order' },
    { id: 'packing', label: 'Packing' },
    { id: 'packed_ready', label: 'DC Pending' },
    { id: 'invoice_pending', label: 'Invoice Pending' },
    { id: 'ewaybill_pending', label: 'E-way Bill Pending' },
    { id: 'invoice_ready', label: 'QC Pending' },
    { id: 'qc_passed', label: 'Confirm Dispatch' },
    { id: 'shipped', label: 'Shipped' },
    { id: 'partially_shipped', label: 'Partially Shipped' },
    { id: 'in_transit', label: 'In Transit' },
    { id: 'delivered', label: 'Delivered' },
    { id: 'canceled', label: 'Canceled' }
  ];

  const getEffectiveStatus = (order) => {
    const orderDispatches = dispatchesMap[order.id] || [];
    if (orderDispatches.length === 0) return order.status;

    // Check if status should be "Partial"
    const statuses = orderDispatches.map(d => d.dispatchStatus);
    const uniqueStatuses = [...new Set(statuses)];

    if (uniqueStatuses.length > 1 || order.status === 'partially_shipped') {
        // Find the "highest" status reached by any MOC
        const hasDelivered = statuses.includes('delivered');
        const hasDispatched = statuses.includes('dispatched') || statuses.includes('in_transit');
        const hasDC = statuses.includes('invoice_ready') || statuses.includes('ewaybill_complete') || statuses.includes('dc_confirmed') || statuses.includes('invoice_pending') || statuses.includes('ewaybill_pending');
        const hasPacked = statuses.includes('packed_ready');
        const hasApproved = statuses.includes('moc_approved');

        if (hasDelivered) return 'part_delivered';
        if (hasDispatched) return 'part_dispatched';
        if (hasDC) return 'part_dc_ready';
        if (hasPacked) return 'part_packed';
        if (hasApproved) return 'part_approved';
    }

    return order.status;
  };

  const renderStatus = (status) => {
    switch (status) {
      case 'part_delivered':
        return <span style={{ color: '#059669', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Partially Delivered</span>;
      case 'part_dispatched':
        return <span style={{ color: '#4338ca', display: 'flex', alignItems: 'center', gap: '5px' }}><Truck size={14} /> Part Dispatch</span>;
      case 'part_dc_ready':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px' }}><FileText size={14} /> Part DC Ready</span>;
      case 'part_packed':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Partially Packed</span>;
      case 'part_approved':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Partially Approved</span>;
      case 'draft':
        return <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Draft</span>;
      case 'in_que':
        return <StatusBadge status="in_que" />;
      case 'moc_in_progress':
        return <StatusBadge status="moc_in_progress" />;
      case 'pending_ops_approval':
        return <StatusBadge status="pending_ops_approval" />;
      case 'pending_finance':
        return <StatusBadge status="pending_finance" />;
      case 'moc_approved':
        return <StatusBadge status="moc_approved" />;
      case 'packing_queued':
        return <StatusBadge status="packing_queued" />;
      case 'packing_scheduled':
        return <StatusBadge status="packing_scheduled" />;
      case 'pending_approval':
        return <span style={{ color: '#2563eb', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Waiting Approval</span>;
      case 'rejected':
        return <span style={{ color: '#c2410c', display: 'flex', alignItems: 'center', gap: '5px' }}><AlertCircle size={14} /> Rejected</span>;
      case 'confirmed_order':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px' }}><FileText size={14} /> Confirmed Order</span>;
      case 'packing':
        return <span style={{ color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: '5px' }}><FileText size={14} /> Packing</span>;
      case 'shipped':
        return <span style={{ color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Shipped</span>;
      case 'dispatched':
        return <StatusBadge status="dispatched" />;
      case 'in_transit':
        return <StatusBadge status="in_transit" />;
      case 'partially_shipped':
        return <span style={{ color: '#4338ca', display: 'flex', alignItems: 'center', gap: '5px' }}><Truck size={14} /> Partially Shipped</span>;
      case 'delivered':
        return <StatusBadge status="delivered" />;
      case 'canceled':
        return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '5px' }}><X size={14} /> Canceled</span>;
      case 'fin_rejected':
        return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '5px' }}><X size={14} /> Fin Rejected</span>;
      case 'fin_hold':
        return <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Fin Hold</span>;
      case 'packing_hold':
        return <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Packing Hold</span>;
      case 'packing_rejected':
        return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '5px' }}><X size={14} /> Packing Rejected</span>;
      case 'packed_ready':
        return <StatusBadge status="packed_ready" />;
      case 'invoice_pending':
        return <StatusBadge status="invoice_pending" />;
      case 'ewaybill_pending':
        return <StatusBadge status="ewaybill_pending" />;
      case 'invoice_ready':
        return <StatusBadge status="invoice_ready" />;
      case 'ewaybill_complete':
        return <StatusBadge status="ewaybill_complete" />;
      case 'dc_confirmed':
      case 'qc_pending':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Invoice Ready</span>;
      case 'qc_passed':
        return <span style={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> QC Passed</span>;
      default:
        return status ? status.replace(/_/g, ' ') : status;
    }
  };

  const formatDate = (dateString) => {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const getActionConfig = (order) => {
    if (!isSalesUser(user)) return null;

    if (order.status === 'draft') {
      return {
        label: 'Edit',
        style: editActionBtnStyle,
        icon: null,
        onClick: () => navigate(`/orders/${order.id}`, { state: { mode: 'edit' } })
      };
    }

    if (order.status === 'rejected') {
      return {
        label: 'Revise',
        style: editActionBtnStyle,
        icon: null,
        onClick: () => navigate(`/orders/${order.id}`, { state: { mode: 'edit' } })
      };
    }

    if (isSalesManager(user) && order.status === 'pending_approval') {
      return {
        label: 'Preview',
        style: previewActionBtnStyle,
        icon: <Eye size={14} />,
        onClick: () => navigate(`/orders/${order.id}`)
      };
    }

    return {
      label: 'Preview',
      style: previewActionBtnStyle,
      icon: null,
      onClick: () => navigate(`/orders/${order.id}`)
    };
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <h2 style={{ marginBottom: '20px', fontSize: isMobile ? '20px' : '24px' }}>Order Hub ({filteredAndSortedOrders.length})</h2>

      {/* Filter Section */}
      <div style={{ ...filterSectionStyle, gap: isMobile ? '10px' : '15px', padding: isMobile ? '12px' : '15px' }}>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>From Date</label>
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>To Date</label>
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Customer</label>
          <input type="text" name="customerName" value={filters.customerName} onChange={handleFilterChange} placeholder="Search Customer" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Salesperson</label>
          <input type="text" name="salesPerson" value={filters.salesPerson} onChange={handleFilterChange} placeholder="Search Salesperson" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Order Type</label>
          <select name="orderType" value={filters.orderType} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            <option value="Sales">Sales</option>
            <option value="Rental">Rental</option>
          </select>
        </div>
        {(isSalesUser(user) || isAdminUser(user)) && (
          <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
            <label style={filterLabelStyle}>Status</label>
            <select name="status" value={filters.status} onChange={handleFilterChange} style={filterInputStyle}>
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Waiting Approval</option>
              <option value="rejected">Rejected</option>
              <option value="in_que">In Que</option>
              <option value="confirmed_order">Confirmed Order</option>
              {isAdminUser(user) && (
                <>
                  <option value="packing">Packing</option>
                  <option value="packed_ready">DC Pending</option>
                  <option value="ewaybill_pending">E-way Bill Pending</option>
                  <option value="invoice_ready">Invoice Ready</option>
                  <option value="qc_passed">QC Passed</option>
                  <option value="shipped">Shipped</option>
                  <option value="partially_shipped">Partially Shipped</option>
                  <option value="in_transit">In Transit</option>
                  <option value="delivered">Delivered</option>
                </>
              )}
              <option value="fin_rejected">Fin Rejected</option>
              <option value="fin_hold">Fin Hold</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
        )}
        {isOpsUser(user) && (
          <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
            <label style={filterLabelStyle}>Status</label>
            <select name="status" value={filters.status} onChange={handleFilterChange} style={filterInputStyle}>
              <option value="">All</option>
              <option value="confirmed_order">Confirmed Order</option>
              <option value="packing">Packing</option>
              <option value="qc_pending_group">QC Pending</option>
              <option value="qc_passed">Confirm Dispatch</option>
              <option value="shipped">Shipped</option>
              <option value="partially_shipped">Partially Shipped</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button type="button" onClick={handleClearFilters} style={clearFilterButtonStyle}>
            Clear Filters
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <p style={{ padding: '30px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
          No orders found. Click 'New Order' to create your first one.
        </p>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '800px' : 'auto' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle} onClick={() => handleSort('createdAt')} >Date{getSortIcon('createdAt')}</th>
                <th style={{...thStyle, textAlign:'left'}} onClick={() => handleSort('customerName')}>Customer{getSortIcon('customerName')}</th>
                <th style={thStyle} onClick={() => handleSort('orderNumber')}>Order Number{getSortIcon('orderNumber')}</th>
                <th style={thStyle} onClick={() => handleSort('salesPerson')}>Salesperson{getSortIcon('salesPerson')}</th>
                <th style={thStyle} onClick={() => handleSort('orderType')}>Type{getSortIcon('orderType')}</th>
                <th style={thStyle} onClick={() => handleSort('orderValue')}>Value{getSortIcon('orderValue')}</th>
                <th style={thStyle} onClick={() => handleSort('status')}>Status{getSortIcon('status')}</th>
                {isSalesUser(user) && <th style={thStyle}>Action</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order) => {
                const actionConfig = getActionConfig(order);

                return (
                <tr 
                  key={order.id} 
                  style={{ borderBottom: '1px solid #e5e7eb' }}
                  className="clickable-row"
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <td style={tdStyle}>{formatDate(order.createdAt)}</td>
                 <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{order.customerName}</div>
                    <div style={{fontSize: '11px', color: '#6b7280'}}>{order.subject?.substring(0, 40) || 'No Subject'}...</div>
              </td>
              <td style={tdStyle}>
                <strong style={{fontSize: '13px'}}>{order.orderNumber || order.id.substring(0, 8).toUpperCase()}</strong>
              </td>
                  <td style={tdStyle}>{order.salesPerson || 'N/A'}</td>
                  <td style={tdStyle}>{order.orderType}</td>
                  <td style={tdStyle}>{order.orderValue ? `₹${order.orderValue.toLocaleString('en-IN')}` : '0'}</td>
                  <td style={tdStyle}>
                    <div 
                      style={{ 
                        position: 'relative', 
                        display: 'inline-block',
                        cursor: (isAdminUser(user) && order.status !== 'draft') ? 'pointer' : 'default'
                      }}
                      onClick={(e) => {
                        if (isAdminUser(user) && order.status !== 'draft') {
                          e.stopPropagation();
                          setActiveDropdownId(activeDropdownId === order.id ? null : order.id);
                        }
                      }}
                    >
                      <div 
                        className={(isAdminUser(user) && order.status !== 'draft') ? "hover-highlight" : ""}
                        style={{ 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {renderStatus(getEffectiveStatus(order))}
                        {isAdminUser(user) && order.status !== 'draft' && <ChevronDown size={12} color="#6b7280" />}
                      </div>

                      {isAdminUser(user) && activeDropdownId === order.id && (
                        <div 
                          ref={dropdownRef}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                            zIndex: 1000,
                            minWidth: '160px',
                            marginTop: '5px',
                            overflow: 'hidden'
                          }}
                        >
                          {statusOptions.map((opt) => (
                            <div
                              key={opt.id}
                              className="hover-highlight"
                              style={{
                                padding: '10px 15px',
                                fontSize: '13px',
                                textAlign: 'left',
                                color: order.status === opt.id ? '#2563eb' : '#374151',
                                fontWeight: order.status === opt.id ? '600' : 'normal',
                                borderBottom: '1px solid #f3f4f6'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleQuickStatusUpdate(order.id, opt.id);
                              }}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  {isSalesUser(user) && (
                    <td style={tdStyle}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          actionConfig.onClick();
                        }}
                        style={actionConfig.style}
                      >
                        {actionConfig.icon}
                        {actionConfig.label}
                      </button>
                    </td>
                  )}
                </tr>
              )})}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f8fafc' }}>
                <td colSpan={isSalesUser(user) ? 8 : 7} style={{ padding: '12px 15px', textAlign: 'right', fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                  Total Value: <span style={{ color: '#0f766e' }}>₹{filteredOrderValueTotal.toLocaleString('en-IN')}</span>
                </td>
              </tr>
            </tfoot>
          </table>

          {/* Pagination Controls */}
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            padding: '15px', 
            borderTop: '1px solid #e5e7eb',
            gap: isMobile ? '15px' : '0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: '10px', fontSize: '13px', color: '#374151' }}>Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ padding: '5px 8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px' }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={paginationButtonStyle}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#374151' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={paginationButtonStyle}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const thStyle = { padding: '12px 15px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', cursor: 'pointer' };
const tdStyle = { padding: '10px 15px', fontSize: '13px', textAlign: 'center' };
const linkStyle = { color: '#1f2937', textDecoration: 'none', display: 'block' };
const paginationButtonStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '5px', backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: '13px' };
const previewActionBtnStyle = { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '8px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' };
const editActionBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px' };
const clearFilterButtonStyle = { padding: '9px 14px', border: '1px solid #cbd5e1', borderRadius: '6px', backgroundColor: 'white', color: '#0f172a', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' };

const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', minWidth: '150px', flex: '1' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const filterInputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };

export default Orders;
